// /en/expat-relocation — EN hub. Phase P-EXPAT-RELOCATION.
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ExpatTopic, ExpatCategory } from "@/data/expat-relocation";
import { EXPAT_TOPICS_LITE, EXPAT_LITE_BY_CATEGORY, EXPAT_CAT_LABELS_TH, EXPAT_CAT_LABELS_EN } from "@/data/expat-index.gen";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Home, Phone, MessageCircle, ArrowRight, Sparkles, ShieldCheck, Building2, Wallet, GraduationCap, HeartPulse } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/expat-relocation";
const TITLE = "Moving to Thailand — Housing, Banking, Schools, Healthcare Guide 2026 | NYC";
const DESC =
  "Complete expat relocation guide to Thailand: condo rental & foreign-quota purchase, BOI 1-rai land. Talk to a specialist today; no obligation, fast reply.";

export const Route = createFileRoute("/en/expat-relocation/")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/expat-relocation", "en")}, { name: "description", content: ctaDesc(DESC, "/en/expat-relocation", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/expat-relocation", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/expat-relocation", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "moving to Thailand, expat relocation Thailand, condo rental foreigner, foreign quota condo, open bank account Thailand, BOI 1 rai land, international schools Bangkok, Bumrungrad, Samitivej, Non-MT visa, Thailand social security, worldwide income tax Thailand 2024" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/expat-relocation", PATH)],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: TITLE, description: DESC, url, inLanguage: "en-US",
              mainEntity: {
                "@type": "ItemList", numberOfItems: EXPAT_TOPICS_LITE.length,
                itemListElement: EXPAT_TOPICS_LITE.map((t, i) => ({
                  "@type": "ListItem", position: i + 1, name: t.titleEn,
                  url: `${SITE}/en/expat-relocation/${t.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Expat Relocation Concierge",
              serviceType: "Expat Relocation, Housing, Banking, Schools, Healthcare Setup",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "8000", priceCurrency: "THB", description: "Full relocation concierge package" },
            }),
          },
          faqPageSchema([
            { q: "What do I need to prepare to move to Thailand?", a: "5 essentials: (1) Right Non-Immigrant visa (Non-B / LTR / DTV / Retirement / Marriage) (2) Housing (rent THB 8K–80K/month or buy under foreign quota) (3) Bank account (Kasikorn/SCB/Bangkok Bank) (4) Schools (if you have children — international 200K–1.2M THB/year) (5) Health insurance (local 15K–50K or international 60K–300K THB/year)." },
            { q: "Should foreigners rent or buy a condo?", a: "< 2 years = rent / 2–5 years = rent + consider DTV/LTR / 5+ years + budget ≥ THB 5M = buy under 49% Foreign Quota." },
            { q: "Easiest bank to open as an expat?", a: "Kasikorn K Plus (best mobile app and English UI), Bangkok Bank (largest ATM network, required for Wise transfers), and SCB Easy (biometric login) are the three most expat-friendly. All require a Non-Immigrant visa (B, O, LTR, Elite, or DTV) plus a valid work permit or Certificate of Residence from Immigration. NYC drafts the CoR and books your branch appointment in one workflow." },
            { q: "How many international schools in Thailand?", a: "180+ (Bangkok 130, Phuket 8, Chiang Mai 6, others 40+) — IB / British / American / Singaporean / Australian curriculum / tuition THB 200K–1.2M/year." },
            { q: "Which expat health insurance?", a: "Local (Pacific Cross, AXA Thailand) THB 15K–50K/year — mid-tier network. International (Cigna, BUPA, Allianz) THB 60K–300K/year — covers Bumrungrad/Samitivej + worldwide emergency. LTR visa requires ≥ USD 50,000 coverage." },
            { q: "Emergency numbers in Thailand?", a: "1669 (medical emergency, free ambulance) / 1155 Tourist Police (English/Chinese/Russian) / 191 police / 199 fire+rescue / private hospital: Bumrungrad 02 011 5222, Bangkok Hospital 1719, Samitivej 02 022 2222." },
          ]),
        ]),
      ],
    };
  },
  component: ExpatHubEn,
});

const CAT_ICONS: Record<ExpatCategory, typeof Building2> = {
  housing: Building2, banking: Wallet, schools: GraduationCap, healthcare: HeartPulse,
};

function ExpatHubEn() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span><span>Expat Relocation</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Home className="mr-1 inline h-3 w-3" /> Expat Concierge · {EXPAT_TOPICS_LITE.length} topics · updated 2026
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Moving to Thailand — the Complete Expat Guide</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Housing · Banking & Finance · Schools & Education · Healthcare & Insurance — 24 topics with real costs, document checklists, legal references, and every step foreigners need before relocating to Thailand.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">Moving to Thailand — 5 essentials</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              (1) Right visa — DTV/LTR/Non-B/Non-O Retirement/Marriage — chosen by age, income, and goals (2) Housing — rent THB 8K–80K/month, or buy under 49% Foreign Quota + FET form (3) Bank account — Kasikorn/SCB/Bangkok Bank + Certificate of Residence (4) Schools (if children) — international 200K–1.2M/year, public EP/MEP 30K–80K/year (5) Health insurance — local 15K–50K/year or international 60K–300K/year (LTR requires ≥ USD 50,000). NYC handles all 5 in one concierge package.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/visa-wizard" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Sparkles className="h-4 w-4" /> Start Visa Wizard
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Browse by category</h2>
          <p className="mt-2 text-sm text-muted-foreground">4 categories · 24 topics · click any topic for real costs, documents, and steps</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(EXPAT_LITE_BY_CATEGORY) as ExpatCategory[]).map((cat) => {
              const Icon = CAT_ICONS[cat];
              return (
                <div key={cat}>
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-primary">
                    <Icon className="h-5 w-5" /> {EXPAT_CAT_LABELS_EN[cat]}
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{EXPAT_LITE_BY_CATEGORY[cat].length} topics</span>
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {EXPAT_LITE_BY_CATEGORY[cat].map((t) => (
                      <Link
                        key={t.slug}
                        to="/en/expat-relocation/$topic"
                        params={{ topic: t.slug }}
                        className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                        data-track-zone="expat-hub-en"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{t.icon}</span>
                          <div className="font-bold text-sm">{t.titleEn}</div>
                        </div>
                        <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{t.taglineEn}</div>
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-muted-foreground">{t.costEn.split(" ")[0]}</span>
                          <span className="inline-flex items-center gap-1 font-semibold text-primary">View <ArrowRight className="h-3 w-3" /></span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />Related services</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Wizard — pick a visa in 60s", d: "Answer 5 questions → 7-visa scoring", to: "/en/visa-wizard" as const },
              { t: "Compare 7 Thai visas", d: "Tourist · DTV · LTR · Non-O · Non-B · Non-ED · Marriage", to: "/en/visa-matrix" as const },
              { t: "DTV 5-year Digital Nomad", d: "Workcation · Freelancer · 9 categories", to: "/en/dtv" as const },
              { t: "LTR 10-year HSP/WFTP", d: "Work from Thailand Professional", to: "/en/ltr" as const },
              { t: "Compliance — TM30 / 90-day", d: "Re-entry / Overstay / TDAC", to: "/en/visa-compliance" as const },
              { t: "Company Setup", d: "BOI / Treaty of Amity / Thai Ltd", to: "/en/company-setup" as const },
              { t: "Work Permit + Non-B", d: "15 nationalities BOI fast-track", to: "/en/work-permit" as const },
              { t: "Driving licence exchange", d: "12 major nationalities", to: "/en/driving-license" as const },
              { t: "Translation + Apostille", d: "60+ Hague countries", to: "/en/translate" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" /></main>
  );
}
